import { Button } from 'reactstrap';
import { HiTrash, HiPencil, HiCheckCircle, HiXCircle } from 'react-icons/hi2';
import { MdRestore } from 'react-icons/md';
import styled from 'styled-components';

import { useSelector } from 'react-redux';
import {
  commitDraftRow,
  startEditingRow,
  cancelDraftRow,
  deleteRow,
  restoreRow,
  setValidationErrors,
  clearValidationErrors,
  setRowEditValidationModal,
} from '../../../features/mappingAdmin/mappingAdminSlice';
import {
  FieldMappingDataRow,
  FixingProxyMappingDataRow,
  LegalEntityMappingDataRow,
  MappingAdminState,
} from '../../../common/domain/mappingAdmin';
import {
  COLUMN_ID,
  DUPLICATE_VALIDATION_COLUMN_IDS,
  NON_EMPTY_VALIDATION_COLUMN_IDS,
  Tab,
  VALIDATION_FIELD_DISPLAY_NAMES,
} from '../constants';
import { ActionCellProps } from '../interfaces';

import { findDuplicateId } from '../utils';
import { IRowNode } from 'ag-grid-community';

const ActionButton = styled(Button)`
  background-color: transparent !important;
  border: none !important;
`;

const ICON_SIZE = 20;

type ValidationResult =
  | { passed: true }
  | { passed: false; failedFields: string[]; message?: string };

export const ActionCell = ({ params, username, dispatch }: ActionCellProps) => {
  const { activeTab, draftRow, deletedRows } = useSelector(
    (state: any) => state.mappingAdmin as MappingAdminState,
  );
  const rowData = { ...params.data };

  const onEdit = () => {
    dispatch(
      startEditingRow({
        rowData,
        tabType: activeTab as Tab,
      }),
    );
  };

  const onDelete = () => {
    dispatch(deleteRow({ id: rowData.id }));
  };

  const onRestore = () => {
    dispatch(restoreRow({ id: rowData.id }));
  };

  const rowValidationPassed = (
    currentRowData:
      | FieldMappingDataRow
      | FixingProxyMappingDataRow
      | LegalEntityMappingDataRow,
  ): ValidationResult | undefined => {
    if (!currentRowData) {
      return undefined;
    }

    const validationErrors: string[] = [];

    const fail = (
      rowIds: string[],
      fields: string[],
      message?: string,
    ): ValidationResult => {
      dispatch(
        setValidationErrors({
          rowIds,
          fields,
        }),
      );
      return { passed: false, failedFields: fields, message: message || '' };
    };

    dispatch(clearValidationErrors());
    NON_EMPTY_VALIDATION_COLUMN_IDS[activeTab].forEach((field) => {
      const value = currentRowData[field];
      const isEmpty =
        value == null || (typeof value === 'string' && value.trim() === '');
      if (isEmpty) {
        validationErrors.push(field);
      }
    });

    if (validationErrors.length > 0) {
      return fail(
        [currentRowData.id],
        validationErrors,
        buildRequiredFieldsMessage(validationErrors),
      );
    }

    if (
      new Date(currentRowData[COLUMN_ID.VALID_TO]) <=
      new Date(currentRowData[COLUMN_ID.VALID_FROM])
    ) {
      return fail(
        [currentRowData.id],
        [COLUMN_ID.VALID_FROM, COLUMN_ID.VALID_TO],
        'Valid From must be before Valid To.',
      );
    }

    const fieldsDuplicateChecked = DUPLICATE_VALIDATION_COLUMN_IDS[activeTab];
    const notesToCheck: any[] = [];
    params.api.forEachNode((node: IRowNode) => {
      if (node.data.id !== currentRowData.id) {
        notesToCheck.push(node.data);
      }
    });
    const duplicatedNodeId = findDuplicateId(
      currentRowData,
      notesToCheck,
      activeTab,
    );
    if (duplicatedNodeId) {
      return fail(
        [currentRowData.id, duplicatedNodeId],
        fieldsDuplicateChecked,
        'Row with the same primary key already exists.',
      );
    }
    return { passed: true };
  };

  const buildRequiredFieldsMessage = (failedFields: string[]): string => {
    const names = failedFields
      .map((f) => VALIDATION_FIELD_DISPLAY_NAMES[f] || f)
      .join(', ');
    return `${names} must be populated.`;
  };

  const onOk = () => {
    const currentRowData = draftRow?.data;
    if (!currentRowData) {
      return;
    }
    const result = rowValidationPassed(currentRowData);
    if (result === undefined) return;
    if (!result.passed) {
      dispatch(
        setRowEditValidationModal({
          isOpen: true,
          message: `Please correct the validation errors before saving. ${
            result.message || ''
          }`,
        }),
      );
      return;
    }
    params.api.stopEditing(false);
    dispatch(commitDraftRow({ username }));
  };

  const onCancel = () => {
    params.api.stopEditing(true);
    dispatch(cancelDraftRow());
  };

  if (rowData.id === draftRow?.data.id) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <ActionButton
          color="danger"
          size="sm"
          onClick={onCancel}
          title="Cancel"
        >
          <HiXCircle size={ICON_SIZE} className="text-danger" />
        </ActionButton>
        <ActionButton color="success" size="sm" onClick={onOk} title="OK">
          <HiCheckCircle size={ICON_SIZE} className="text-success" />
        </ActionButton>
      </div>
    );
  }
  if (rowData.id in deletedRows) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <ActionButton
          color="primary"
          size="sm"
          onClick={onRestore}
          title="Restore row"
        >
          <MdRestore size={ICON_SIZE} className="text-primary" />
        </ActionButton>
      </div>
    );
  }
  return (
    <div className="d-flex align-items-center justify-content-center h-100">
      <ActionButton
        size="sm"
        onClick={onEdit}
        title="Edit row"
        disabled={draftRow !== null}
      >
        <HiPencil size={ICON_SIZE} className="text-primary" />
      </ActionButton>
      <ActionButton
        size="sm"
        onClick={onDelete}
        title="Remove row"
        disabled={draftRow !== null}
      >
        <HiTrash size={ICON_SIZE} className="text-danger" />
      </ActionButton>
    </div>
  );
};
